// src/App.js — app shell with TutorPanel mounted as a docked right
// sidebar above <Routes>. The runtime provider hoists the tutor's
// session above the route layer so the conversation persists across
// every navigation. See M0 in ~/.claude/plans/lazy-chasing-shannon.md.
import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import Header from './components/common/Header';
import ErrorBoundary from './components/common/ErrorBoundary';
import NotFound from './components/common/NotFound';
import { ToastProvider } from './components/common/Toast';
import AuthWrapper from './components/AuthWrapper';
import LandingPage from './components/LandingPage';
import CourseDashboard from './components/CourseDashboard';
import Quiz from './components/Quiz';
import QuizResults from './components/QuizResults';
import CourseCreator from './components/CourseCreator';
import MyCourses from './components/MyCourses';
import CourseManagement from './components/CourseManagement';
import ConceptEditor from './components/ConceptEditor';
import DomainContentViewer from './components/DomainContentViewer';
import MasteryDashboard from './components/MasteryDashboard';
import DailyPractice from './components/DailyPractice';
import LearningInsights from './components/LearningInsights';
import TutorPanel, { isTutorSuppressed } from './components/TutorPanel';
import KitchenSink from './components/KitchenSink';
import DevChat from './components/DevChat';
import DevWorkbench from './components/DevWorkbench';
import WorkbenchPage from './components/workbench/WorkbenchPage';
import { TutorRuntimeProvider, useTutor } from './contexts/TutorRuntimeContext';
import { ConfirmProvider } from './components/ui/ConfirmDialog';

const PANEL_PAD_RIGHT = 400; // matches PANEL_WIDTH in TutorPanel.jsx

// Reset window scroll on every route change. React Router 6 doesn't
// do this by default — without it, navigating from a scrolled page
// (e.g. quiz results scrolled down) into a new page (e.g. /my-courses)
// keeps the old scrollY, hiding the new page's title under the fixed
// header. The autoFocus on the tutor composer also nudges the document
// down on first paint; this resets it.
function ScrollToTop() {
  const { pathname } = useLocation();
  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

// Wraps page content so it shifts left when the panel is open. Keep
// content out from under the fixed-position panel without re-flowing
// the panel itself.
function PageWithTutorShift({ children }) {
  const { panelOpen } = useTutor();
  const { pathname } = useLocation();
  // The tutor hides itself on some routes (isTutorSuppressed); reserving its 400px there left a black strip.
  const reserve = panelOpen && !isTutorSuppressed(pathname);
  return (
    <div
      className="transition-[padding] duration-200"
      style={{ paddingRight: reserve ? `${PANEL_PAD_RIGHT}px` : 0 }}
    >
      {children}
    </div>
  );
}

// /tutor route is kept for back-compat with old links / bookmarks.
// Opens the panel and redirects to the home surface so the user
// lands somewhere normal with the tutor visible on the right.
function TutorLegacyRedirect() {
  const { openPanel } = useTutor();
  React.useEffect(() => { openPanel(); }, [openPanel]);
  return <Navigate to="/" replace />;
}

function App() {
  return (
    <ConfirmProvider>
    <Router>
      <ScrollToTop />
      <ErrorBoundary>
        <ToastProvider>
          <div className="min-h-screen bg-surface-muted">
            {/* Review gate — purely presentational, mounted outside auth so
                automated sweeps reach it. */}
            <Routes>
              <Route path="/kitchen-sink" element={<KitchenSink />} />
              <Route path="/dev-chat" element={<DevChat />} />
              <Route path="/dev-workbench" element={<DevWorkbench />} />
              <Route path="*" element={null} />
            </Routes>
            {['/kitchen-sink', '/dev-chat', '/dev-workbench'].includes(window.location.pathname) ? null : (
            <AuthWrapper>
              <TutorRuntimeProvider>
                <Routes>
                  {/* Full-screen immersive routes (no Header). M0 scope:
                      these don't get the panel shift — they need the
                      whole viewport. Panel still renders; user can
                      collapse it for these surfaces. */}
                  <Route path="/course/:courseId/workbench/:domain" element={<WorkbenchPage />} />
                  <Route path="/course/:courseId/mastery" element={<MasteryDashboard />} />
                  <Route path="/course/:courseId/insights" element={<LearningInsights />} />
                  <Route path="/course/:courseId/practice" element={<DailyPractice />} />

                  {/* All other routes with Header + panel-aware shift */}
                  <Route path="*" element={
                    <PageWithTutorShift>
                      <Header />
                      <main>
                        <Routes>
                          <Route path="/" element={<LandingPage />} />
                          <Route path="/tutor" element={<TutorLegacyRedirect />} />
                          <Route path="/my-courses" element={<MyCourses />} />
                          <Route path="/create-course" element={<CourseCreator />} />
                          <Route path="/course/:courseId" element={<CourseDashboard />} />
                          <Route path="/course/:courseId/manage" element={<CourseManagement />} />
                          <Route path="/course/:courseId/manage/:domain/:concept" element={<ConceptEditor />} />
                          <Route path="/course/:courseId/content/:domain" element={<DomainContentViewer />} />
                          <Route path="/course/:courseId/quiz/:domain" element={<Quiz />} />
                          <Route path="/course/:courseId/quick-quiz" element={<Quiz />} />
                          <Route path="/course/:courseId/quiz/:domain/results/:quizId" element={<QuizResults />} />
                          <Route path="*" element={<NotFound />} />
                        </Routes>
                      </main>
                    </PageWithTutorShift>
                  } />
                </Routes>
                {/* Panel mounts above all routes — survives navigation. */}
                <TutorPanel />
              </TutorRuntimeProvider>
            </AuthWrapper>
            )}
          </div>
        </ToastProvider>
      </ErrorBoundary>
    </Router>
    </ConfirmProvider>
  );
}

export default App;
